/**
 * Wall API client. The Worker is the only thing this talks to — it owns the
 * secret key, redeems the responseToken via /api/v1/siteverify, and is the
 * only component allowed to decide whether a post goes up.
 */

import type { ReactionCounts } from "./reactions";

export interface Post {
  id: string;
  who: string;
  text: string;
  at: number;
  mine?: boolean;
}

/**
 * Why a post did not go up. The distinction matters more than the message:
 * `bot` came from the ShieldWall verdict, `content` came from the wall's own
 * rules. Conflating them makes state 12 unreadable against state 6.
 */
export type RejectKind = "bot" | "content" | "rate" | "expired" | "network";

export type PostOutcome =
  | { ok: true; post: Post; pending: boolean }
  | {
      ok: false;
      kind: RejectKind;
      message: string;
      reasonCodes: string[];
      retryAfterMs?: number;
    };

export interface SubmitInput {
  name: string;
  message: string;
  /** responseToken from the widget. Null on the degraded path (state 7). */
  token: string | null;
}

const API = "/api";

/**
 * @param since epoch ms — omit for the full wall, pass the newest known
 *   timestamp to fetch only what has arrived since.
 * @param signal lets an in-flight poll be dropped when the tab is hidden or
 *   the component unmounts, so a slow response cannot land out of order.
 */
export interface WallSnapshot {
  posts: Post[];
  /** Always the full map — reactions land on existing posts, so `since` cannot filter them. */
  reactions: ReactionCounts;
}

export async function fetchWall(since = 0, signal?: AbortSignal): Promise<WallSnapshot> {
  const url = since ? `${API}/wall?since=${since}` : `${API}/wall`;
  const res = await fetch(url, { headers: { accept: "application/json" }, signal });
  if (!res.ok) throw new Error(`wall fetch failed: ${res.status}`);
  const body = (await res.json()) as Partial<WallSnapshot>;
  return { posts: body.posts ?? [], reactions: body.reactions ?? {} };
}

/** Add or take back one reaction. Resolves false if it did not stick. */
export async function react(
  postId: string,
  emoji: string,
  id: string,
  on: boolean,
): Promise<boolean> {
  try {
    const res = await fetch(`${API}/react`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ postId, emoji, clientId: id, on }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Server trims to the same bound, so the client count cannot drift upward. */
export const WALL_LIMIT = 200;

/**
 * Merges polled posts into what is already on screen. Keyed by id so a post
 * that arrives in a poll having already been inserted optimistically after
 * submit does not appear twice.
 */
export function mergePosts(current: Post[], incoming: Post[]): Post[] {
  if (incoming.length === 0) return current;

  const byId = new Map(current.map((p) => [p.id, p]));
  let changed = false;
  for (const p of incoming) {
    if (byId.has(p.id)) continue;
    byId.set(p.id, p);
    changed = true;
  }
  if (!changed) return current;

  return [...byId.values()].sort((a, b) => b.at - a.at).slice(0, WALL_LIMIT);
}

export async function submitPost(input: SubmitInput): Promise<PostOutcome> {
  let res: Response;
  try {
    res = await fetch(`${API}/wall`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  } catch {
    return {
      ok: false,
      kind: "network",
      message: "The wall could not be reached. Check your connection and post again.",
      reasonCodes: [],
    };
  }

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  // 201 went up. 202 was accepted for human review — the degraded path, where
  // the widget never loaded. Both succeeded; only one is on the wall.
  if (res.status === 201 || res.status === 202) {
    return {
      ok: true,
      post: { ...(body.post as Post), mine: true },
      pending: res.status === 202,
    };
  }

  const reasonCodes = Array.isArray(body["reason-codes"])
    ? (body["reason-codes"] as string[])
    : [];
  const message = typeof body.message === "string" ? body.message : "";

  if (res.status === 429) {
    return {
      ok: false,
      kind: "rate",
      message,
      reasonCodes,
      retryAfterMs: Number(res.headers.get("retry-after") ?? 60) * 1000,
    };
  }

  // 403 = ShieldWall said no. 419 = token expired or already redeemed.
  // 422 = the wall's own content rules said no. Three different states.
  if (res.status === 419) return { ok: false, kind: "expired", message, reasonCodes };
  if (res.status === 403) return { ok: false, kind: "bot", message, reasonCodes };
  return { ok: false, kind: "content", message, reasonCodes };
}

/** Fire-and-forget false-positive report. Highest-value signal on the site. */
export async function reportFalsePositive(payload: {
  note: string;
  reasonCodes: string[];
  score: number | null;
}): Promise<void> {
  try {
    await fetch(`${API}/false-positive`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    /* the report is best-effort; never block the user on it */
  }
}
